import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  canCreateExternalClient,
  canDeleteExternalClient,
} from '@selectors/actions/externalClientActions';
import { getAuthorizedActions } from '@selectors/featureProps';
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { useListRowActions } from '@hooks/useListRowActions';

import { Button, Dropdown, ListView } from '@commons/utils/styledLibraryComponents';
import { GENERIC_MODAL_CANCEL_BUTTON } from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import EmptyState from '@commons/EmptyState';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import { getClientInfo } from '@selectors/client';

import { externalClient as externalClientService } from '@services/externalClient';

import { exportExternalClientCatalog, exportExternalClients } from './utils/export';
import { getActions, getRowActions } from './utils/actions';
import { getColumns } from './utils/columns';

import { Content, HeaderContainer, PageContainer } from './styledComponents';

const EXTERNAL_CLIENTS_PATH = '/external/clients';
const EXTERNAL_CLIENT_CREATION_PATH = '/external/clients/create';

const DEFAULT_ORDER_BY = 'name';
const DEFAULT_ORDER_TYPE = 'asc';
const DEFAULT_MAX_PER_PAGE = 20;
const FIRST_PAGE = 1;

const enableServerSideQuerying = () => true;

const fetchCatalog = async (externalClientId, productCount) => {
  const fetchCatalogPage = (limit) =>
    externalClientService.getExternalClientCatalog(
      externalClientId,
      {
        page: FIRST_PAGE,
        limit,
        search: '',
        orderBy: DEFAULT_ORDER_BY,
        orderType: DEFAULT_ORDER_TYPE,
      },
      {},
    );

  const { catalog } = await fetchCatalogPage(productCount);

  return catalog ?? [];
};

export const ExternalClients = (props) => {
  const {
    history,
    stores,
    storeName,
    hasCentralKitchens,
    currency,
    authorizedActions,
    canDeleteClient,
    userLanguageCode,
    displayErrorMessage,
    openModal,
    pageLoading,
    pageLoaded,
    match: { path },
  } = props;

  const [externalClients, setExternalClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccessibleExternalClients, setHasAccessibleExternalClients] = useState(true);

  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderType, setOrderType] = useState(DEFAULT_ORDER_TYPE);
  const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
  const [maxPerPage, setMaxPerPage] = useState(DEFAULT_MAX_PER_PAGE);

  const [selectedStores, setSelectedStores] = useState(stores);
  const [selectedExternalClients, setSelectedExternalClients] = useState([]);

  const { refreshIndex, performDeletion } = useListRowActions({
    deleteResource: externalClientService.deleteExternalClient,
    deleteSuccessMessage: i18next.t('EXTERNAL_CLIENTS.LIST.DELETE_SUCCESS'),
    deleteErrorMessage: i18next.t('EXTERNAL_CLIENTS.LIST.DELETE_ERROR'),
    displayedRowCount: externalClients.length,
    currentPage,
    setCurrentPage,
    setIsLoading,
    setSelectedRows: setSelectedExternalClients,
  });

  const isMonoStore = stores.length <= 1;
  const pluralStoreName = getClientStoreNameTranslation(storeName, true);

  const goToCreation = () => {
    history.push(EXTERNAL_CLIENT_CREATION_PATH);
  };

  const goToClientDetails = ({ id }) => {
    history.push(`${EXTERNAL_CLIENTS_PATH}/${id}/details`);
  };

  const handleExportList = useCallback(async () => {
    if (selectedExternalClients.length) {
      exportExternalClients(selectedExternalClients);

      return;
    }

    const selectedStoreIds = selectedStores.map(({ id }) => id);

    if (!selectedStoreIds.length || !totalCount) {
      return;
    }

    pageLoading();

    try {
      const { externalClients: data } = await externalClientService.getPaginatedExternalClients(
        { page: FIRST_PAGE, limit: totalCount, search, orderBy, orderType },
        { storeIds: selectedStoreIds },
      );

      exportExternalClients(data ?? []);
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.LIST.EXPORT_ERROR'));
    } finally {
      pageLoaded();
    }
  }, [selectedExternalClients, selectedStores, totalCount, search, orderBy, orderType]);

  const handleExportCatalog = useCallback(
    async ({ id, name, productCount }) => {
      if (!productCount) {
        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.EXPORT_EMPTY'));

        return;
      }

      pageLoading();

      try {
        const catalog = await fetchCatalog(id, productCount);

        exportExternalClientCatalog(catalog, {
          clientName: name,
          hasCentralKitchens,
          currency,
        });
      } catch {
        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.CATALOG.EXPORT_ERROR'));
      } finally {
        pageLoaded();
      }
    },
    [hasCentralKitchens, currency],
  );

  const handleDeleteClient = useCallback(
    (externalClient) => {
      openModal(
        getConfirmationModal({
          title: i18next.t('EXTERNAL_CLIENTS.LIST.DELETE_MODAL_TITLE'),
          icon: '/images/inpulse/warning-white-small.svg',
          content: i18next.t('EXTERNAL_CLIENTS.LIST.DELETE_MODAL_CONTENT'),
          actions: [
            GENERIC_MODAL_CANCEL_BUTTON(),
            {
              key: 1,
              color: 'inpulse-default',
              label: i18next.t('GENERAL.DELETE'),
              icon: '/images/inpulse/trash-white-small.svg',
              handleClick: () => {
                performDeletion(externalClient);
              },
            },
          ],
        }),
      );
    },
    [performDeletion],
  );

  const columns = useMemo(() => getColumns({ userLanguageCode }), [userLanguageCode]);

  const actions = useMemo(
    () =>
      getActions({
        selectedItems: selectedExternalClients,
        authorizedActions,
        onCreate: goToCreation,
        onExport: handleExportList,
      }),
    [selectedExternalClients, authorizedActions, handleExportList],
  );

  const rowActions = useMemo(
    () =>
      getRowActions({
        onExportCatalog: handleExportCatalog,
        onDelete: handleDeleteClient,
        canDelete: canDeleteClient,
      }),
    [canDeleteClient, handleDeleteClient, handleExportCatalog],
  );

  useEffect(() => {
    setSelectedStores(stores);
  }, [stores]);

  useEffect(() => {
    let isCurrentQuery = true;

    const fetchExternalClients = async () => {
      const selectedStoreIds = selectedStores.map(({ id }) => id);

      if (!selectedStoreIds.length) {
        setExternalClients([]);
        setTotalCount(0);
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      try {
        const { externalClients: data, totalCount: count } =
          await externalClientService.getPaginatedExternalClients(
            { page: currentPage, limit: maxPerPage, search, orderBy, orderType },
            { storeIds: selectedStoreIds },
          );

        if (!isCurrentQuery) {
          return;
        }

        setExternalClients(data ?? []);
        setTotalCount(count ?? 0);

        if (!search && selectedStoreIds.length === stores.length) {
          setHasAccessibleExternalClients((count ?? 0) > 0);
        }
      } catch {
        if (!isCurrentQuery) {
          return;
        }

        displayErrorMessage(i18next.t('EXTERNAL_CLIENTS.LIST.FETCH_ERROR'));

        setExternalClients([]);
        setTotalCount(0);
      } finally {
        if (isCurrentQuery) {
          setIsLoading(false);
        }
      }
    };

    fetchExternalClients();

    return () => {
      isCurrentQuery = false;
    };
  }, [search, orderBy, orderType, currentPage, maxPerPage, selectedStores, refreshIndex]);

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleMaxPerPageChange = (value) => {
    setMaxPerPage(value);
    setCurrentPage(FIRST_PAGE);
  };

  const handleStoreFilterChange = (items) => {
    setSelectedStores(items);
    setCurrentPage(FIRST_PAGE);
  };

  const renderEmptyPerimeterState = () => {
    const canCreate = canCreateExternalClient(authorizedActions);

    return (
      <GeneralEmptyStateListView
        icon={'/images/inpulse/no-data.svg'}
        renderAction={
          canCreate
            ? () => (
                <Button
                  color={'inpulse-default'}
                  handleClick={goToCreation}
                  icon={'/images/inpulse/add-white-small.svg'}
                  label={i18next.t('GENERAL.CREATE')}
                />
              )
            : null
        }
        subtitle={i18next.t(
          canCreate
            ? 'EXTERNAL_CLIENTS.LIST.EMPTY_STATE_SUBTITLE_WITH_CREATION'
            : 'EXTERNAL_CLIENTS.LIST.EMPTY_STATE_SUBTITLE',
          { storeName: pluralStoreName.toLowerCase() },
        )}
        title={i18next.t('EXTERNAL_CLIENTS.LIST.EMPTY_STATE_TITLE', {
          storeName: pluralStoreName.toLowerCase(),
        })}
      />
    );
  };

  const renderList = () => (
    <ListView
      actionOnClick={goToClientDetails}
      actions={actions}
      columns={columns}
      countElements={totalCount}
      data={externalClients}
      defaultCurrentPage={currentPage}
      defaultMaxPerPage={maxPerPage}
      defaultOrderBy={orderBy}
      defaultOrderType={orderType}
      defaultSearchInput={search}
      handleCurrentPageChange={setCurrentPage}
      handleMaxPerPageChange={handleMaxPerPageChange}
      handleOrderByChange={setOrderBy}
      handleOrderTypeChange={setOrderType}
      handleSearchInputChange={handleSearchInputChange}
      isLoading={isLoading}
      languageCode={userLanguageCode}
      minActionsInActionsDropdown={1}
      padding={'24px 24px 0px 24px'}
      placeholderShape={i18next.t('GENERAL.SEARCH')}
      renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
      renderFilterButton={() => (
        <HeaderContainer>
          <Dropdown
            iconSrc={'/images/inpulse/store-black-small.svg'}
            isDisabled={isMonoStore}
            isUniqueSelection={false}
            items={stores}
            placeholder={pluralStoreName}
            selectedItems={selectedStores}
            isRequired
            onSelectionChange={handleStoreFilterChange}
          />
        </HeaderContainer>
      )}
      rowActions={rowActions}
      setSelectedItems={setSelectedExternalClients}
      onQueryParamsChange={enableServerSideQuerying}
    />
  );

  return (
    <PageContainer>
      <NavigationBreadCrumb featurePath={path} />
      <Content>
        {!isLoading && !hasAccessibleExternalClients ? renderEmptyPerimeterState() : renderList()}
      </Content>
    </PageContainer>
  );
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_CLIENTS_PATH,
  );

  const clientInfo = getClientInfo(state.baseReducer.user);

  return {
    stores: state.baseReducer.activeStores,
    storeName: clientInfo.storeName,
    hasCentralKitchens: clientInfo.hasCentralKitchens,
    currency: state.baseReducer.currency,
    userLanguageCode: state.baseReducer.user?.lnkLanguageAccountrel?.code,
    authorizedActions,
    canDeleteClient:
      state.baseReducer.user?.isDeepsight || canDeleteExternalClient(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalClients);
